import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Star, Music, Calendar, Gift, ShoppingBag, Download } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const VIP = () => {
  const [pollAnswer, setPollAnswer] = useState(null);
  const [merchEmail, setMerchEmail] = useState('');
  const [pollSubmitted, setPollSubmitted] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const { user, getToken } = useAuth();
  const navigate = useNavigate();

  const handlePollSubmit = async (answer) => {
    setPollAnswer(answer);
    setPollSubmitted(true);
    
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/poll/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vote: answer.toLowerCase() })
      });
    } catch (error) {
      console.error('Error submitting poll:', error);
    }
  };

  const handleMerchEmailSubmit = (e) => {
    e.preventDefault();
    // TODO: Send email to backend when ready
    console.log('Merch email:', merchEmail);
    alert('Thanks! We\'ll notify you when merch drops! 🔥');
    setMerchEmail('');
  };

  const handleSubscribe = async (tier) => {
    // Check if user is logged in
    if (!user) {
      navigate('/login?redirect=/vip');
      return;
    }

    setSubscribing(true);

    try {
      const token = getToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vip/create-checkout-session?tier=${tier}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Failed to start subscription. Please try again.');
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-b from-zinc-950 via-black to-black overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-red-900/10 via-black to-black"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#0a0a0a_1px,transparent_1px),linear-gradient(to_bottom,#0a0a0a_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-20"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Title */}
          <div className="mb-8">
            <div className="inline-block px-4 py-2 bg-red-600/20 border border-red-500/50 rounded-full text-red-400 text-sm font-semibold mb-6 animate-pulse">
              EXCLUSIVE MEMBERSHIP
            </div>
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white mb-4">
              NUMERO UNO
              <span className="block text-red-500 mt-2 drop-shadow-[0_0_30px_rgba(220,38,38,0.8)]">
                VIP MEMBERSHIP
              </span>
            </h1>
            <p className="text-2xl text-gray-300 font-semibold mt-6">
              Join the Community & Movement
            </p>
          </div>

          {/* Brand Message */}
          <div className="max-w-3xl mx-auto mb-12">
            <p className="text-lg text-gray-400 leading-relaxed">
              Numero Uno Sounds is more than a label — it's a <span className="text-red-400 font-semibold">community</span>, a <span className="text-red-400 font-semibold">movement</span>, and a <span className="text-red-400 font-semibold">drum & bass family</span>. We don't see supporters as numbers on a screen; we see real people. Where others chase statistics, we build relationships — family growing, not numbers rising.
            </p>
          </div>

          {/* Two-Tier Pricing */}
          {/* Single VIP Tier - £4.99 */}
          <div className="max-w-2xl mx-auto">
            <div className="bg-gradient-to-br from-red-950 to-zinc-900 border-2 border-red-500 rounded-2xl p-10 relative hover:shadow-[0_0_80px_rgba(220,38,38,0.6)] transition-all duration-300">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-6 py-2 bg-red-500 rounded-full text-sm font-bold">
                VIP ACCESS
              </div>
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold mb-3">VIP MEMBER</h3>
                <div className="text-6xl font-bold text-red-400 mb-2">£4.99</div>
                <div className="text-xl opacity-80">per month</div>
              </div>
              
              <div className="space-y-6 mb-8">
                {/* Upon Joining */}
                <div className="bg-black/30 rounded-xl p-4 border border-red-500/20">
                  <h4 className="text-red-400 font-bold mb-3 flex items-center gap-2">
                    <Music className="w-5 h-5" />
                    Upon Joining
                  </h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300"><strong className="text-white">Choose 3 tracks instantly</strong> from our back catalogue</span>
                    </li>
                  </ul>
                </div>

                {/* Monthly Benefits */}
                <div className="bg-black/30 rounded-xl p-4 border border-red-500/20">
                  <h4 className="text-red-400 font-bold mb-3 flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Every 1st of the Month
                  </h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300"><strong className="text-white">Choose 2 more tracks</strong> from our back catalogue</span>
                    </li>
                  </ul>
                </div>

                {/* Unlimited New Releases */}
                <div className="bg-gradient-to-br from-red-900/20 to-black/30 rounded-xl p-4 border border-red-500/40">
                  <h4 className="text-red-400 font-bold mb-3 flex items-center gap-2">
                    <Download className="w-5 h-5" />
                    Unlimited Throughout the Month
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300"><strong className="text-white">EVERY new release</strong> we put out</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300"><strong className="text-white">Before public release</strong> - get them first!</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300"><strong className="text-white">Exclusive dubplates & bootlegs</strong> included with your subscription</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300"><strong className="text-white">FREE ENTRY to all our events</strong> (Jump Up in the Jungle series)</span>
                    </li>
                  </ul>
                  <div className="mt-4 pt-4 border-t border-red-500/20">
                    <p className="text-sm text-gray-400 italic">
                      All new releases, bootlegs, and exclusive content are included with your subscription as bonus perks - not sold separately.
                    </p>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => handleSubscribe("499")}
                disabled={subscribing}
                className="w-full px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-bold text-lg rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-red-500/50"
              >
                {subscribing ? 'Loading...' : 'Subscribe for £4.99/month'}
              </button>
              
              <p className="text-center text-sm text-gray-500 mt-4">
                Cancel anytime • Billing on 1st of each month
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-zinc-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl sm:text-5xl font-bold text-center mb-4">
            VIP MEMBER BENEFITS
          </h2>
          <div className="w-24 h-1 bg-red-500 mx-auto mb-16"></div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* VIP Dubs */}
            <div className="bg-zinc-900/50 border-2 border-red-500/30 rounded-xl p-8 hover:border-red-500 transition-all duration-300 hover:shadow-[0_0_40px_rgba(220,38,38,0.3)]">
              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 bg-red-500/20 rounded-lg">
                  <Star className="w-8 h-8 text-red-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Exclusive Content</h3>
                  <p className="text-gray-400 text-lg">Included with VIP</p>
                </div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                  <span className="text-gray-300"><strong className="text-white">Dubplates & bootlegs</strong> included with subscription</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                  <span className="text-gray-300"><strong className="text-white">All new releases</strong> before the public</span>
                </li>
              </ul>
            </div>

            {/* Perks */}
            <div className="bg-zinc-900/50 border-2 border-red-500/30 rounded-xl p-8 hover:border-red-500 transition-all duration-300 hover:shadow-[0_0_40px_rgba(220,38,38,0.3)]">
              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 bg-red-500/20 rounded-lg">
                  <Gift className="w-8 h-8 text-red-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Back Catalogue</h3>
                  <p className="text-gray-400 text-lg">Your Choice</p>
                </div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                  <span className="text-gray-300"><strong className="text-white">3 tracks</strong> when you join</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                  <span className="text-gray-300"><strong className="text-white">2 more every month</strong> (1st of the month)</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mt-8">
            {/* Monthly Tracks */}
            <div className="bg-zinc-900/50 border-2 border-red-500/30 rounded-xl p-8 hover:border-red-500 transition-all duration-300 hover:shadow-[0_0_40px_rgba(220,38,38,0.3)]">
              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 bg-red-500/20 rounded-lg">
                  <Calendar className="w-8 h-8 text-red-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Every Month</h3>
                  <p className="text-gray-400 text-lg">On the 1st</p>
                </div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                  <span className="text-gray-300"><strong className="text-white">2 or 3 tracks</strong> of your choice from the back catalogue (depending on tier)</span>
                </li>
              </ul>
            </div>

            {/* New Releases */}
            <div className="bg-zinc-900/50 border-2 border-red-500/30 rounded-xl p-8 hover:border-red-500 transition-all duration-300 hover:shadow-[0_0_40px_rgba(220,38,38,0.3)]">
              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 bg-red-500/20 rounded-lg">
                  <Music className="w-8 h-8 text-red-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Early Releases</h3>
                  <p className="text-gray-400 text-lg">Before everyone else</p>
                </div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                  <span className="text-gray-300"><strong className="text-white">Every new release</strong> instantly - before public drop</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                  <span className="text-gray-300"><strong className="text-white">VIP dubs included</strong> when available</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                  <span className="text-gray-300 text-sm">Excludes custom dubs made for other DJs</span>
                </li>
              </ul>
            </div>

            {/* Future Perks */}
            <div className="bg-zinc-900/50 border-2 border-red-500/30 rounded-xl p-8 hover:border-red-500 transition-all duration-300 hover:shadow-[0_0_40px_rgba(220,38,38,0.3)]">
              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 bg-red-500/20 rounded-lg">
                  <Star className="w-8 h-8 text-red-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Exclusive Perks</h3>
                  <p className="text-gray-400 text-lg">VIP treatment</p>
                </div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                  <span className="text-gray-300"><strong className="text-white">Event discounts</strong> for Jump Up in the Jungle</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                  <span className="text-gray-300"><strong className="text-white">Merch discounts</strong> when merch launches</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                  <span className="text-gray-300"><strong className="text-white">Priority access</strong> to all insider content</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Important Note */}
          <div className="mt-12 p-6 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
            <p className="text-yellow-200 text-center">
              <strong className="text-yellow-400">Important:</strong> Custom dubplates made for individual DJs are exclusive to those DJs only and are NOT included in VIP membership. VIP members can only select from the catalogue releases and bootlegs.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl sm:text-5xl font-bold text-center mb-4">
            HOW IT WORKS
          </h2>
          <div className="w-24 h-1 bg-red-500 mx-auto mb-16"></div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-xl font-bold mb-3">Subscribe</h3>
              <p className="text-gray-400 mb-3">
                Sign up for £4.99/month and get <strong className="text-white">3 tracks instantly</strong>
              </p>
              <ul className="text-sm text-gray-500 text-left max-w-xs mx-auto space-y-1">
                <li>• Then get 2 more tracks on the 1st of each month</li>
                <li>• Excludes custom dubs made for other DJs</li>
              </ul>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-xl font-bold mb-3">Choose Your Tracks</h3>
              <p className="text-gray-400">
                Pick any tracks from our back catalogue using the member portal
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-xl font-bold mb-3">Get Early Access</h3>
              <p className="text-gray-400">
                Receive all new releases before public drop + <strong className="text-white">2 more tracks every 1st</strong>
              </p>
            </div>
          </div>

          {/* Billing Explanation */}
          <div className="mt-16 max-w-3xl mx-auto bg-zinc-900/50 border border-red-500/30 rounded-xl p-8">
            <h3 className="text-2xl font-bold mb-4 text-center">Billing Cycle</h3>
            <p className="text-gray-300 mb-4">
              Subscriptions renew on the <strong className="text-white">1st of every month</strong>, regardless of when you join.
            </p>
            <p className="text-gray-400 text-sm">
              <strong className="text-gray-300">Example:</strong> If you join on the 27th, you'll pay and get 3 tracks immediately. Then on the 1st, your subscription renews and you get 2 more track credits. Every month continues on the 1st.
            </p>
          </div>
        </div>
      </section>

      {/* Merch Coming Soon + Poll */}
      <section className="py-20 bg-zinc-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-zinc-900 to-black border-2 border-green-500/30 rounded-2xl p-12 text-center">
            <ShoppingBag className="w-16 h-16 text-green-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-4">
              MERCH COMING SOON
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Exclusive Numero Uno clothing and merchandise is being developed.<br />
              <strong className="text-green-400">VIP members will receive discounts</strong> when it launches.
            </p>

            {/* Interest Poll */}
            {!pollSubmitted ? (
              <>
                <p className="text-lg text-gray-400 mb-6">
                  Would you be interested in Numero Uno merchandise?
                </p>
                <div className="flex flex-wrap justify-center gap-4 mb-8">
                  <button
                    onClick={() => handlePollSubmit('yes')}
                    className="px-8 py-4 bg-green-600 hover:bg-green-500 text-white rounded-lg font-bold transition-all duration-300 text-lg"
                  >
                    ✅ Yes
                  </button>
                  <button
                    onClick={() => handlePollSubmit('maybe')}
                    className="px-8 py-4 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg font-bold transition-all duration-300 text-lg"
                  >
                    🤔 Maybe
                  </button>
                  <button
                    onClick={() => handlePollSubmit('no')}
                    className="px-8 py-4 bg-gray-600 hover:bg-gray-500 text-white rounded-lg font-bold transition-all duration-300 text-lg"
                  >
                    ❌ Not right now
                  </button>
                </div>
              </>
            ) : (
              <div className="mb-8 p-4 bg-green-600/20 border border-green-500/50 rounded-lg">
                <p className="text-green-400 font-semibold">Thanks for your feedback! 🙏</p>
              </div>
            )}

            {/* Email Capture */}
            <div className="max-w-md mx-auto">
              <p className="text-sm text-gray-400 mb-4">Get notified when merch drops</p>
              <form onSubmit={handleMerchEmailSubmit} className="flex gap-2">
                <input
                  type="email"
                  value={merchEmail}
                  onChange={(e) => setMerchEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="flex-1 px-4 py-3 bg-zinc-800 border border-green-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-500"
                />
                <button
                  type="submit"
                  className="px-6 py-3 bg-green-600 hover:bg-green-500 text-white rounded-lg font-semibold transition-colors duration-300"
                >
                  Notify Me
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            READY TO JOIN THE FAMILY?
          </h2>
          <p className="text-xl text-gray-400 mb-12">
            Become a VIP member and be part of something real
          </p>
          
          <button 
            onClick={handleSubscribe}
            disabled={subscribing}
            className="px-12 py-6 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl font-bold text-2xl hover:from-red-500 hover:to-red-600 transition-all duration-300 shadow-[0_0_40px_rgba(220,38,38,0.6)] hover:shadow-[0_0_60px_rgba(220,38,38,0.9)] hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {subscribing ? 'LOADING...' : user ? 'JOIN VIP — £4.99/MONTH' : 'SIGN IN TO JOIN VIP'}
          </button>
          
          {!user && (
            <p className="text-sm text-gray-500 mt-6">
              You'll be redirected to sign in first
            </p>
          )}
        </div>
      </section>

      {/* Terms Section */}
      <section className="py-16 bg-zinc-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold mb-6 text-center">Terms & Details</h3>
          <div className="space-y-4 text-gray-400 text-sm">
            <p>• Billing renews on the 1st of each month</p>
            <p>• Signup charges immediately and includes instant 2-track benefit</p>
            <p>• Members choose tracks by leaving track names in the Track Choices box during selection</p>
            <p>• Custom dubplates made for individual DJs are exclusive to those DJs only - NOT available to VIP members</p>
            <p>• Cancel anytime (access continues until the end of the paid period)</p>
            <p>• VIP members get early access to all public releases</p>
            <p>• Merch discounts and event perks apply when available</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VIP;
